/**
 * How big this person likes their cards. One value, for the whole app.
 *
 * Zoom used to be stored eight separate ways: five keys inside the Collection
 * preferences blob (cards, card sets, wish list, catalogue, search) and three
 * standalone localStorage keys for the picker, the set grid and the binder
 * grid. Setting it in Explore left the binder none the wiser, which is exactly
 * how it felt to use. The owner: "it's a general preference throughout, tapping
 * one impacts all zooms".
 *
 * That is the right split, and it is the OPPOSITE of sort and filter. Sort is
 * about the task in front of you - value high to low while hunting one Pokemon
 * has nothing to do with how a binder is ordered - so sort and filter stay
 * remembered per place. Zoom is about your eyes, and your eyes do not change
 * between screens.
 *
 * Three steps, and step 0 is the SMALLEST. That direction is load-bearing: the
 * control's uncoloured resting state is the smallest view everywhere in this
 * app, so somebody who learns the button on one screen has learned it on all of
 * them.
 */

export const ZOOM_STEPS = 3;
const KEY = 'cardchase.zoom';

/** Keys zoom used to live in, newest-used first, read once to migrate. */
const LEGACY_COLLECTION_KEY = 'cardchase_collection_prefs';
const LEGACY_COLLECTION_FIELDS = ['cardZoom', 'catalogZoom', 'searchZoom', 'cardsSetsZoom', 'wishListZoom'];
const LEGACY_STANDALONE_KEYS = [
  'cardchase.wishPicker.prefs',
  'cardchase.wishSetGrid.prefs',
  'cardchase.wishBinderGrid.prefs',
  'cardchase.wishMasterBrowse.prefs',
];

export function clampZoom(zoom) {
  const n = Number(zoom);
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(Math.trunc(n), 0), ZOOM_STEPS - 1);
}

/**
 * What the eight old keys said, so nobody's view silently resets to smallest on
 * the release that merges them. The Cards tab is asked first because it is the
 * screen people spend their time on; the rest are a fallback in the order they
 * were most likely to have been touched.
 */
function migratedZoom() {
  try {
    const blob = localStorage.getItem(LEGACY_COLLECTION_KEY);
    if (blob) {
      const saved = JSON.parse(blob);
      for (const field of LEGACY_COLLECTION_FIELDS) {
        if (saved?.[field] != null) return clampZoom(saved[field]);
      }
    }
    for (const key of LEGACY_STANDALONE_KEYS) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const saved = JSON.parse(raw);
      if (saved?.zoom != null) return clampZoom(saved.zoom);
    }
  } catch {
    /* A corrupt preference is not worth an error; the default is fine. */
  }
  return 0;
}

export function readZoom() {
  try {
    if (typeof localStorage === 'undefined') return 0;
    const raw = localStorage.getItem(KEY);
    if (raw === null) {
      const carried = migratedZoom();
      // Written back so the migration happens once rather than on every read.
      try { localStorage.setItem(KEY, String(carried)); } catch { /* fine */ }
      return carried;
    }
    return clampZoom(raw);
  } catch {
    return 0;
  }
}

/**
 * Store it, and tell every mounted grid.
 *
 * Two surfaces can be alive at once - a picker over a binder, a slot grid under
 * a sheet - and localStorage fires no event in the tab that wrote it. Without
 * this the screen you tapped would resize and the one behind it would not, which
 * is the same inconsistency this file exists to remove.
 */
export function writeZoom(zoom) {
  const next = clampZoom(zoom);
  try { localStorage.setItem(KEY, String(next)); } catch { /* fine */ }
  try {
    window.dispatchEvent(new CustomEvent('cc:zoom', { detail: next }));
  } catch {
    /* No window, no listeners to tell. */
  }
  return next;
}

/** The next step round the loop, which is all any control does with it. */
export function nextZoom(zoom) {
  return (clampZoom(zoom) + 1) % ZOOM_STEPS;
}

export const ZOOM_EVENT = 'cc:zoom';
